import { useCallback, useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../core/hooks/storeHooks';
import _ from 'lodash';
import {
  selectSelectedSheetAreas,
  selectSheet,
} from '../../../../../sheet/selectors/sheetSelector';
import {
  Area,
  DateFilterCondition,
  IconSetCategory,
  IconSetType,
  NumberFilterCondition,
  TextFilterCondition,
} from '../../../../../sheet/types';
import {
  convertAreaToAddressString,
  parseSingleAddress,
} from '../../../../../sheet/utils/address/singleAddress';
import { RootState } from '../../../../../core/redux/store';
import {
  ValidationCriteria,
  ValidationRule,
  ValidationRuleCriteria,
  ValidationRuleSettings,
} from '../../../../../sheet/types';
import { convertParsedAddressToArea } from '../../../../../sheet/utils/address/address';
import { handleSheetOperations } from '../../../../../sheet/redux/sagas/root/rootActions';
import { SheetCommandType } from '../../../../../sheet/types/commands/sheetCommands/sheetCommands';
import { v4 as uuid } from 'uuid';

export type ChangeCriteria = (criteria: ValidationCriteria) => void;
export type UpdateRule = (rule: Partial<ValidationRuleCriteria>) => void;
export type UpdateSettings = (
  settings: Partial<ValidationRuleSettings>
) => void;

// #region hook
export default (
  onClose: () => void,
  sheetId: string,
  existingRule?: ValidationRule
) => {
  const dispatch = useAppDispatch();
  const sheetItems = useAppSelector((state) =>
    selectSheetItems(state, sheetId)
  );

  const [range, setRange] = useState<string>('');
  const [rule, setRule] = useState<ValidationRule>(
    createRuleFromCriteria('', ValidationCriteria.IconSets)
  );
  const [settings, setSettings] = useState<Partial<ValidationRuleSettings>>({});

  const changeCriteria: ChangeCriteria = useCallback(
    (criteria: ValidationCriteria) => {
      const newRule = createRuleFromCriteria(rule.id, criteria);
      setRule(newRule);
    },
    [setRule]
  );

  const updateRule: UpdateRule = useCallback(
    (newRule: Partial<ValidationRuleCriteria>) => {
      const updatedRule: ValidationRule = _.merge(rule, newRule);
      setRule({
        ...updatedRule,
      });
    },
    [rule]
  );

  const updateSettings: UpdateSettings = useCallback(
    (newSettings: Partial<ValidationRuleSettings>) => {
      setSettings({
        ...settings,
        ...newSettings,
      });
    },
    [settings]
  );

  useEffect(() => {
    setExistingRule(sheetItems, setRule, setSettings, setRange, existingRule);
  }, []);

  const saveRule = () => {
    // TODO: Add Rule Validation
    const areas = range
      .split(',')
      .map((subRange) =>
        convertParsedAddressToArea(
          parseSingleAddress(subRange),
          sheetItems.maxColIndex,
          sheetItems.maxRowIndex
        )
      );

    const id = rule.id.length ? rule.id : uuid();

    const fullRule: ValidationRule = {
      ...rule,
      ...settings,
      areas,
      id,
    };

    dispatch(
      handleSheetOperations({
        sheetId,
        type: SheetCommandType.ApplyDataValidation,
        rules: [fullRule],
      })
    );
    onClose();
  };

  return {
    range,
    rule,
    settings,
    setRange,
    changeCriteria,
    updateRule,
    updateSettings,
    saveRule,
  };
};
// #endregion

// #region helpers
function convertAreasToString(
  sheetName: string,
  maxColIndex: number,
  maxRowIndex: number,
  areas: Area[]
) {
  let range = '';
  areas.forEach((area, index) => {
    const isLast = index === areas.length - 1;
    const address = convertAreaToAddressString(
      area,
      maxColIndex,
      maxRowIndex,
      sheetName
    );
    range += address;
    if (!isLast) range += ',';
  });

  return range;
}

function setExistingRule(
  sheet: ReturnType<typeof selectSheetItems>,
  setRule: (rule: ValidationRule) => void,
  setSettings: UpdateSettings,
  setRange: (range: string) => void,
  existingRule?: ValidationRule
) {
  const { selectedAreas, name, maxColIndex, maxRowIndex } = sheet;
  const defaultSettings = {
    showHelpText: true,
    showAlertWhenInvalid: true,
  };
  let areas = selectedAreas;
  if (existingRule) {
    const newRule = _.cloneDeep(existingRule);
    const {
      ignoreBlanks,
      showHelpText,
      helpText,
      showAlertWhenInvalid,
      invalidAlert,
      invalidMessage,
      markInvalidContent,
      ...baseRule
    } = newRule;
    setRule(baseRule);

    setSettings({
      ...defaultSettings,
      ignoreBlanks,
      showHelpText,
      helpText,
      showAlertWhenInvalid,
      invalidAlert,
      invalidMessage,
      markInvalidContent,
    });
    areas = newRule.areas;
  }
  const range = convertAreasToString(name, maxColIndex, maxRowIndex, areas);

  setRange(range);
}

function selectSheetItems(state: RootState, sheetId: string) {
  const sheet = selectSheet(state, sheetId);
  const {
    name,
    maxRowIndex,
    maxColIndex,
    dataValidationsMap,
    dataValidationRules,
    dataValidationMatrix,
  } = sheet ?? {};

  return {
    selectedAreas: selectSelectedSheetAreas(state, sheetId),
    name,
    maxColIndex,
    maxRowIndex,
    dataValidationsMap,
    dataValidationRules,
    dataValidationMatrix,
  };
}

function createRuleFromCriteria(
  baseId: string,
  criteria: ValidationCriteria
): ValidationRule {
  const base = {
    id: baseId,
    areas: [],
  };
  let listRule: ValidationRuleCriteria = {
    criteria: ValidationCriteria.List,
    list: [],
    showList: true,
  };
  switch (criteria) {
    case ValidationCriteria.Date:
      listRule = { criteria, condition: DateFilterCondition.IsValid };
      break;
    case ValidationCriteria.Number:
    case ValidationCriteria.TextLength:
      listRule = { criteria, condition: NumberFilterCondition.LessThan };
      break;
    case ValidationCriteria.Text:
      listRule = {
        criteria,
        condition: TextFilterCondition.TextContains,
        firstValue: '',
      };
      break;
    case ValidationCriteria.CellRange:
      listRule = {
        criteria,
        showList: true,
      };
      break;
    case ValidationCriteria.Checkbox:
      listRule = { criteria, checkedValue: '', uncheckedValue: '' };
      break;
    case ValidationCriteria.Files:
      listRule = { criteria };
      break;
    case ValidationCriteria.IconSets:
      listRule = {
        criteria,
        category: IconSetCategory.Delivery,
        showList: true,
        setType: IconSetType.Single,
      };
  }

  return {
    ...base,
    ...listRule,
  };
}
// #endregion
